import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";

export default function Accessibility() {
  const page = webPageSchema({
    path: "/accessibility",
    title: "Accessibility Statement | Shariq Refai, MD, MBA",
    description:
      "Accessibility statement for shariqrefai.com: WCAG 2.2 Level AA conformance, our evaluation method, and how to report any accessibility barrier you find.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Accessibility", path: "/accessibility" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Accessibility Statement | Shariq Refai, MD, MBA"
        description="Accessibility statement for shariqrefai.com: WCAG 2.2 Level AA conformance, our evaluation method, and how to report any accessibility barrier you find."
        url="/accessibility"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Accessibility", path: "/accessibility" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Accessibility</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Accessibility Statement
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            Our commitment to making shariqrefai.com usable by as many
            people as possible, and how to reach us if something gets in
            your way.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Effective date: May 25, 2026.</strong>
          </p>

          <h2>Our Commitment</h2>
          <p>
            Shariq Refai, MD, MBA is committed to making shariqrefai.com
            accessible to people with disabilities. We believe digital
            accessibility is both a legal and an ethical obligation, and we
            work to make sure this website can be perceived, operated,
            understood, and used by as many people as possible, including
            people who rely on assistive technologies such as screen
            readers, screen magnifiers, voice-recognition software, switch
            devices, and keyboard-only navigation.
          </p>

          <h2>Conformance Status</h2>
          <p>
            shariqrefai.com is designed and developed to conform with the{" "}
            <a
              href="https://www.w3.org/TR/WCAG22/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Web Content Accessibility Guidelines (WCAG) 2.2, Level AA
            </a>
            . WCAG 2.2 AA is the de facto technical standard used to
            evaluate accessibility under U.S. Section 508 and to assess
            compliance with Title III of the Americans with Disabilities
            Act (ADA) for places of public accommodation, including
            websites.
          </p>
          <p>
            At this time, we self-evaluate the site as{" "}
            <strong>substantially conformant</strong> with WCAG 2.2 Level
            AA. "Substantially conformant" means most of the website meets
            the standard, with known limitations noted below. Accessibility
            is an ongoing effort, and our goal is full conformance.
          </p>

          <h2>What's Been Built In</h2>
          <ul>
            <li>Semantic HTML, with one <code>&lt;h1&gt;</code> per page and a clear heading hierarchy.</li>
            <li>A "Skip to main content" link as the first focusable element on every page.</li>
            <li>Keyboard operability for all interactive elements (links, buttons, forms, menus), with no keyboard traps.</li>
            <li>A highly visible focus indicator for keyboard users on every focusable element (meets WCAG 2.4.11 and 2.4.13).</li>
            <li>Scroll-padding so that focus moved by skip link, anchor, or programmatic navigation isn't hidden behind the sticky header (WCAG 2.4.11 Focus Not Obscured).</li>
            <li>ARIA landmarks (<code>banner</code>, <code>main</code>, <code>contentinfo</code>) and accessible names where needed.</li>
            <li>Descriptive link text and <code>aria-current</code> on the active navigation item; state is not communicated by color alone.</li>
            <li>Form fields with persistent visible labels programmatically associated with inputs, with native browser validation and <code>autocomplete</code> hints for common fields (WCAG 1.3.5).</li>
            <li>Touch targets sized for comfortable use on mobile (meets WCAG 2.5.8 Target Size minimum).</li>
            <li>Mobile menu that's keyboard accessible, closes on <kbd>Esc</kbd>, returns focus to its trigger, and uses <code>inert</code> on background content while open so focus doesn't leak behind the overlay.</li>
            <li>Alt text on meaningful images and <code>aria-hidden</code> on purely decorative graphics.</li>
            <li>Responsive layouts that reflow to a 320px viewport without loss of content or functionality (WCAG 1.4.10 Reflow).</li>
            <li>Color combinations chosen to meet or exceed WCAG AA contrast ratios for body text (4.5:1) and non-text UI components (3:1).</li>
            <li>Status messages (such as the contact form confirmation) announced to assistive technology via <code>role="status"</code> with <code>aria-live</code> (WCAG 4.1.3).</li>
            <li>Honoring the operating-system <code>prefers-reduced-motion</code> setting to minimize non-essential animation and transitions.</li>
            <li>External links open in a new tab with <code>rel="noopener noreferrer"</code> and an accessible label noting they open in a new tab.</li>
          </ul>

          <h2>Compatibility Statement</h2>
          <p>
            shariqrefai.com is designed to be compatible with the
            following assistive technologies and browsers:
          </p>
          <ul>
            <li>Recent versions of NVDA and JAWS on Windows.</li>
            <li>VoiceOver on macOS and iOS.</li>
            <li>TalkBack on Android.</li>
            <li>Recent versions of Chrome, Firefox, Safari, and Edge.</li>
            <li>Keyboard-only navigation, switch devices, and voice-control software (Dragon, Voice Control).</li>
            <li>Browser zoom up to 200% and OS-level text scaling.</li>
          </ul>
          <p>
            The website may not function reliably with browsers more than
            three major versions out of date.
          </p>

          <h2>Technical Specifications</h2>
          <p>
            Accessibility on this site relies on the following
            technologies, which must be supported by your browser and any
            assistive technology in use:
          </p>
          <ul>
            <li>HTML</li>
            <li>WAI-ARIA</li>
            <li>CSS</li>
            <li>JavaScript</li>
          </ul>

          <h2>Evaluation Method</h2>
          <p>
            The accessibility of this website is reviewed through a
            combination of:
          </p>
          <ul>
            <li>Self-evaluation by the development team using WCAG 2.2 Level AA as the working standard.</li>
            <li>Automated tooling, including browser accessibility audits and Lighthouse checks.</li>
            <li>Manual keyboard-only testing of primary user flows.</li>
            <li>Screen reader spot-checks across new pages and templates.</li>
            <li>Color-contrast verification of body text, interactive controls, and focus indicators.</li>
          </ul>
          <p>
            Reviews are performed during major releases and on a recurring
            basis as part of editorial maintenance. Where third-party audit
            results become available, they will be referenced here.
          </p>

          <h2>Known Limitations</h2>
          <p>
            Despite our best efforts, some aspects of the site may not
            fully meet WCAG 2.2 Level AA at all times. Known limitations
            include:
          </p>
          <ul>
            <li>
              <strong>Embedded third-party content.</strong> Media players,
              external profile widgets, or publisher embeds linked from
              this site may not fully meet the same standards. Where this
              is the case, we provide alternative ways to access the same
              information (such as a direct link or a text summary).
            </li>
            <li>
              <strong>Third-party fonts.</strong> Typography is loaded from
              Google Fonts. If Google Fonts is unavailable in your region
              or blocked, the site falls back to system fonts that may
              affect line length and visual hierarchy.
            </li>
            <li>
              <strong>PDFs and downloadable documents.</strong> Older PDFs
              that may be linked from this site (for example, reference
              materials) might not be fully tagged for screen readers. We
              are working to remediate or replace these on request.
            </li>
          </ul>
          <p>
            If you encounter any other accessibility issue not listed
            here, please report it so it can be added and prioritized.
          </p>

          <h2>Reporting an Accessibility Barrier</h2>
          <p>
            If you encounter any part of this site that's difficult or
            impossible to use with your assistive technology, that fails
            to meet WCAG 2.2 Level AA, or that you'd like improved, please
            tell us. We treat accessibility reports as a priority.
          </p>
          <p>
            <strong>How to report:</strong>
          </p>
          <ul>
            <li>
              Email{" "}
              <a href="mailto:accessibility@shariqrefai.com">
                accessibility@shariqrefai.com
              </a>{" "}
              with the subject line "Accessibility report".
            </li>
            <li>
              Or use the <Link href="/contact">contact page</Link> and
              select an appropriate inquiry category.
            </li>
          </ul>
          <p>
            <strong>What to include, if you can:</strong>
          </p>
          <ul>
            <li>The page URL where you encountered the issue.</li>
            <li>A brief description of the barrier (for example, "the menu won't open with the keyboard," or "the contrast on this button is too low to read").</li>
            <li>The assistive technology, operating system, and browser you're using.</li>
            <li>Whether the issue prevented you from completing what you were trying to do.</li>
          </ul>
          <p>
            <strong>Our response commitment:</strong> We aim to acknowledge
            accessibility reports within five business days, provide an
            initial assessment within ten business days, and resolve
            substantiated WCAG 2.2 Level AA issues as quickly as
            reasonably possible based on scope and complexity. For urgent
            barriers that prevent access to essential information, we'll
            work to provide an alternative means of access while a
            permanent fix is in progress.
          </p>

          <h2>Accessibility Coordinator</h2>
          <p>
            Accessibility for shariqrefai.com is coordinated by the
            editorial and development team for Dr. Refai. The point of
            contact for all accessibility matters, including formal
            complaints, is{" "}
            <a href="mailto:accessibility@shariqrefai.com">
              accessibility@shariqrefai.com
            </a>
            .
          </p>

          <h2>Formal Complaints</h2>
          <p>
            If you believe an accessibility barrier on this website has
            not been adequately addressed after contacting us, you may
            file a formal complaint with the United States Department of
            Justice, Civil Rights Division, under Title III of the
            Americans with Disabilities Act. We would prefer the
            opportunity to resolve the issue first, and we treat that as
            our obligation, not as a courtesy.
          </p>

          <h2>Alternative Means of Access</h2>
          <p>
            If a specific piece of content on this site is inaccessible to
            you, we'll work with you to provide the same information in an
            alternative format (for example, a plain-text version of a
            page, a transcript of audio or video content, or a structured
            email response to a question). Request alternative access by
            emailing{" "}
            <a href="mailto:accessibility@shariqrefai.com">
              accessibility@shariqrefai.com
            </a>
            .
          </p>

          <h2>An Ongoing Effort</h2>
          <p>
            Accessibility is an ongoing effort, not a one-time milestone.
            We work to maintain and improve accessibility over time as
            standards evolve and as we receive feedback from readers,
            users of assistive technology, and accessibility audits. This
            statement is reviewed at least annually and after any
            significant change to the website.
          </p>

          <h2>Crisis Resources</h2>
          <p>
            This site is not a substitute for clinical care. If you are in
            crisis or feel unsafe, call or text{" "}
            <a href="tel:988">988</a> in the United States, call{" "}
            <a href="tel:911">911</a>, or go to the nearest emergency
            room. Outside the United States, contact your local emergency
            services.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
